import { useEffect, useMemo, useRef, useState } from "react";
import {
  limpiarActividad,
  useActividad,
  type EventoActividad,
  type NivelActividad,
} from "./novaActividad";

/**
 * Log de actividad de Nova, estilo terminal: una línea por evento (consulta, propuesta, escritura,
 * error), con marca de tiempo y color por severidad. Filtros por nivel y un "pin" de autoscroll que
 * mantiene la vista pegada al último evento (o la suelta para leer hacia atrás). Sin dependencias:
 * lee del store `novaActividad`.
 */

const NIVELES: { nivel: NivelActividad; etiqueta: string; color: string }[] = [
  { nivel: "info", etiqueta: "info", color: "var(--fc-color-text-muted)" },
  { nivel: "exito", etiqueta: "ok", color: "#4ade80" },
  { nivel: "aviso", etiqueta: "aviso", color: "var(--fc-color-accent, #d4a017)" },
  { nivel: "error", etiqueta: "error", color: "var(--fc-color-danger)" },
];

const COLOR: Record<NivelActividad, string> = {
  info: "var(--fc-color-text-muted)",
  exito: "#4ade80",
  aviso: "var(--fc-color-accent, #d4a017)",
  error: "var(--fc-color-danger)",
};

const GLIFO: Record<NivelActividad, string> = {
  info: "·",
  exito: "✓",
  aviso: "!",
  error: "✕",
};

function hora(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function NovaTerminal({ className }: { className?: string }) {
  const eventos = useActividad();
  const [ocultos, setOcultos] = useState<Set<NivelActividad>>(new Set());
  const [pin, setPin] = useState(true);
  const cuerpoRef = useRef<HTMLDivElement | null>(null);

  const visibles = useMemo(
    () => eventos.filter((e) => !ocultos.has(e.nivel)),
    [eventos, ocultos],
  );

  // Autoscroll al fondo cuando el pin está activo y llegan eventos nuevos.
  useEffect(() => {
    if (pin && cuerpoRef.current) {
      cuerpoRef.current.scrollTop = cuerpoRef.current.scrollHeight;
    }
  }, [visibles, pin]);

  // Si el usuario hace scroll hacia arriba, soltamos el pin; si vuelve al fondo, lo re-activamos.
  function alScroll() {
    const el = cuerpoRef.current;
    if (!el) return;
    const alFondo = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setPin(alFondo);
  }

  function alternarNivel(nivel: NivelActividad) {
    setOcultos((prev) => {
      const next = new Set(prev);
      if (next.has(nivel)) next.delete(nivel);
      else next.add(nivel);
      return next;
    });
  }

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-panel border border-border ${className ?? ""}`}
      style={{ background: "var(--fc-color-surface)" }}
    >
      <header
        className="flex flex-wrap items-center gap-2 px-3 py-2"
        style={{ borderBottom: "1px solid var(--fc-color-border)", background: "var(--fc-color-surface-raised)" }}
      >
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#ff5f56" }} />
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#ffbd2e" }} />
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#27c93f" }} />
        </span>
        <span className="font-data text-2xs font-semibold uppercase tracking-wider" style={{ color: "var(--fc-color-text-muted)" }}>
          Actividad de Nova
        </span>
        <div className="ml-auto flex items-center gap-1">
          {NIVELES.map((n) => {
            const activo = !ocultos.has(n.nivel);
            return (
              <button
                key={n.nivel}
                type="button"
                onClick={() => alternarNivel(n.nivel)}
                className="rounded-pill px-2 py-0.5 font-data text-2xs transition-opacity"
                title={`Mostrar/ocultar ${n.etiqueta}`}
                style={{
                  border: `1px solid ${activo ? n.color : "var(--fc-color-border)"}`,
                  color: activo ? n.color : "var(--fc-color-text-muted)",
                  opacity: activo ? 1 : 0.5,
                  background: "transparent",
                }}
              >
                {n.etiqueta}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setPin((v) => !v)}
            className="ml-1 rounded-control px-2 py-0.5 font-data text-2xs"
            title={pin ? "Autoscroll fijado (clic para soltar)" : "Autoscroll suelto (clic para fijar)"}
            style={{
              border: "1px solid var(--fc-color-border)",
              color: pin ? "var(--fc-color-action)" : "var(--fc-color-text-muted)",
              background: "transparent",
            }}
          >
            {pin ? "📌 fijo" : "soltar"}
          </button>
          <button
            type="button"
            onClick={() => limpiarActividad()}
            className="rounded-control px-2 py-0.5 font-data text-2xs"
            title="Limpiar el registro"
            style={{ border: "1px solid var(--fc-color-border)", color: "var(--fc-color-text-muted)", background: "transparent" }}
          >
            limpiar
          </button>
        </div>
      </header>

      <div
        ref={cuerpoRef}
        onScroll={alScroll}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-2 font-data text-xs leading-relaxed"
        style={{ background: "var(--fc-color-surface)" }}
      >
        {visibles.length === 0 ? (
          <p style={{ color: "var(--fc-color-text-muted)" }}>
            <span style={{ color: "var(--fc-color-action)" }}>nova@farmacenter</span>:~$ esperando actividad…
          </p>
        ) : (
          visibles.map((e) => <Linea key={e.id} evento={e} />)
        )}
      </div>
    </div>
  );
}

function Linea({ evento }: { evento: EventoActividad }) {
  return (
    <div className="flex items-baseline gap-2 whitespace-pre-wrap">
      <span className="flex-none tabular-nums" style={{ color: "var(--fc-color-text-muted)", opacity: 0.7 }}>
        {hora(evento.ts)}
      </span>
      <span className="flex-none" style={{ color: COLOR[evento.nivel] }}>
        {GLIFO[evento.nivel]}
      </span>
      <span style={{ color: "var(--fc-color-text)" }}>
        {evento.texto}
        {evento.detalle ? <span style={{ color: "var(--fc-color-text-muted)" }}> · {evento.detalle}</span> : null}
      </span>
    </div>
  );
}
